import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import models from '../data/models.json';
import type { Model } from '../types.type';
import { ROUTER_KEYS } from '../../common/consts/app-keys.const';
import CharactersSection from '../../common/components/characters/characters.component';
import { SearchIcon } from '../../common/components/icons/search-icon.component';
import { CloseIcon } from '../../common/components/icons/close-icon.component';
import { NotFoundIcon } from '../../common/components/icons/not-found-icon.component';
import Pagination from '../components/ui/pagination/Pagination';
import { preloadImages } from '../../util/preloadImages';

const MODELS_PER_PAGE = 12;

preloadImages(models.map((m) => m.images[0].url));

type FilterId = 'free' | 'premium';

// Consistent horizontal layout for every band of content on the page.
// Headings, filter pills, and grids all use the same wrapper so they
// share an identical left edge — fixes the previous misalignment
// caused by `justify-center` on the grid while headings were left-aligned.
const SECTION_PADDING = 'px-[24px] md:px-[72px] max-w-[1500px] mx-auto w-full';
const GRID_CLASSES =
  'grid gap-[32px] grid-cols-[repeat(auto-fill,minmax(280px,1fr))]';

const ModelCard: React.FC<{ model: Model }> = ({ model }) => {
  const { t } = useTranslation();
  return (
    <Link to={`${ROUTER_KEYS.MODELS}/${model.id}`}>
      <div className="w-full">
        <div className="group relative w-full h-[240px]">
          <div className="absolute bottom-0 right-0 w-full h-full z-10 pointer-events-none origin-bottom-right transition-transform duration-500 ease-in-out group-hover:rotate-3">
            <img
              src={model.images[0].url}
              alt={model.name}
              loading="eager"
              className="w-full h-full object-cover rounded-[22px] border-2 border-border-brand shadow-[0_4px_10px_rgba(42,60,68,0.05)]"
            />
            {model.premium && (
              <span className="absolute top-[12px] right-[12px] inline-flex items-center rounded-[999px] px-[10px] py-[3px] text-[11px] font-display font-bold uppercase tracking-[0.06em] text-[#3a2a00] bg-[linear-gradient(135deg,#ffd76b,#f5a623)] shadow-[0_2px_8px_rgba(245,166,35,0.35)] pointer-events-none">
                {t('library.premiumBadge')}
              </span>
            )}
          </div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-blue-100 rounded-[22px] z-0 origin-bottom-right transition-transform duration-500 ease-in-out group-hover:-rotate-3" />
        </div>
        <h3 className="mt-[12px] text-base-black font-body text-[18px] font-medium">
          {model.name}
        </h3>
      </div>
    </Link>
  );
};

const FilterPill: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-[999px] px-[16px] py-[6px] text-[13px] font-display font-semibold border-2 transition-all duration-150
      ${
        active
          ? 'bg-blue-500 text-black border-blue-600 shadow-[0_0_0_3px_rgba(93,225,255,0.18)]'
          : 'bg-transparent text-content-muted border-border-brand hover:border-blue-link hover:text-blue-link'
      }`}
  >
    {label}
  </button>
);

const ModelsListContainer = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<FilterId>>(new Set());

  const toggleFilter = (id: FilterId) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const term = searchTerm.trim().toLowerCase();

  // Empty filter set = show every category. Otherwise only the categories
  // whose pill is active are shown.
  const showFree = activeFilters.size === 0 || activeFilters.has('free');
  const showPremium = activeFilters.size === 0 || activeFilters.has('premium');

  const filteredFreeModels = useMemo(
    () =>
      showFree
        ? models.filter(
            (m) => !m.premium && (!term || m.name.toLowerCase().includes(term))
          )
        : [],
    [term, showFree]
  );

  const filteredPremiumModels = useMemo(
    () =>
      showPremium
        ? models.filter(
            (m) => m.premium && (!term || m.name.toLowerCase().includes(term))
          )
        : [],
    [term, showPremium]
  );

  const totalPages = Math.ceil(filteredFreeModels.length / MODELS_PER_PAGE);

  const paginatedFreeModels = useMemo(() => {
    const start = (currentPage - 1) * MODELS_PER_PAGE;
    return filteredFreeModels.slice(start, start + MODELS_PER_PAGE);
  }, [filteredFreeModels, currentPage]);

  const noResults =
    filteredFreeModels.length === 0 &&
    filteredPremiumModels.length === 0;

  return (
    <>
      {/* Header band: title + search + filter pills, all on a single section
          so they share the same left edge as the rig grids below. */}
      <div className={`${SECTION_PADDING} md:mt-[32px]`}>
        <div className="flex flex-col items-start md:flex-row md:items-center justify-start md:justify-between w-full">
          <h1 className="text-base-black font-display text-[32px] font-semibold">
            {t('library.label')}
          </h1>
          <div className="relative w-full max-w-[345px] md:max-w-[299px] mt-[16px] md:mt-0">
            <div className="absolute inset-y-0 left-[12px] flex items-center pointer-events-none">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder={t('library.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-[42px] pr-[40px] py-[10px] rounded-[32px] border-2 border-border-brand
                bg-surface shadow-[0px_2px_10px_0px_rgba(42,60,68,0.05)]
                text-content-muted font-body text-[16px] font-regular placeholder:text-slate-400
                focus:shadow-[0_0_0_4px_rgba(93,225,255,0.20)] focus:bg-surface-base focus:outline-none"
            />
            {searchTerm && (
              <button
                type="button"
                className="absolute inset-y-0 right-[12px] flex items-center justify-center"
                onClick={() => setSearchTerm('')}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {/* Filter pills — multi-select; an empty set means "show all". */}
        <div className="flex flex-wrap items-center gap-[10px] mt-[24px]">
          <FilterPill
            label={t('library.filterFree')}
            active={activeFilters.has('free')}
            onClick={() => toggleFilter('free')}
          />
          <FilterPill
            label={t('library.filterPremium')}
            active={activeFilters.has('premium')}
            onClick={() => toggleFilter('premium')}
          />
        </div>
      </div>

      {noResults ? (
        <div className="w-full flex flex-col items-center justify-center gap-[16px] py-[60px] px-[16px] text-center">
          <NotFoundIcon />
          <p className="text-gray-900 text-center font-body text-[16px] font-medium max-w-[345px] md:max-w-[414px]">
            {term
              ? t('library.searchNoResults', { term: searchTerm })
              : t('library.filterNoResults')}
          </p>
        </div>
      ) : (
        <>
          {filteredPremiumModels.length > 0 && (
            <section className={`${SECTION_PADDING} mt-[40px]`}>
              <h2 className="text-base-black font-display text-[22px] font-semibold">
                {t('library.premiumLabel')}
              </h2>
              <p className="mt-[6px] mb-[20px] text-content-muted font-body text-[14px] font-regular max-w-[600px]">
                {t('library.premiumSubtitle')}
              </p>
              <div className={GRID_CLASSES}>
                {filteredPremiumModels.map((model) => (
                  <ModelCard key={model.id} model={model} />
                ))}
              </div>
            </section>
          )}

          {filteredFreeModels.length > 0 && (
            <section className={`${SECTION_PADDING} mt-[56px]`}>
              <h2 className="text-base-black font-display text-[22px] font-semibold mb-[20px]">
                {t('library.freeLabel')}
              </h2>
              <div className={GRID_CLASSES}>
                {paginatedFreeModels.map((model) => (
                  <ModelCard key={model.id} model={model} />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              )}
            </section>
          )}
        </>
      )}

      <CharactersSection />
    </>
  );
};

export default ModelsListContainer;
